// DynamicForm.jsx   (quasi-schema-driven client-side form)
import React, { useState } from "react";
import "./dynamicForm.css";
import NarrowInputField from "../../components/input/new/narrowInputField";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import FileUploader from "../../components/input/new/fileUploader";
import TextAreaWithCount from "../../components/input/new/textAreaWithCount";
import IconCheckbox from "../../components/input/new/iconCheckbox";
import RadioButton from "../../components/input/new/radioButton";
import NarrowDatePicker from "../../components/input/new/narrowDatePicker";
import { supabase } from "../../supabaseClient";

/**
 * @param {Object}       props
 * @param {Object|Array} props.schema        – parsed form JSON
 * @param {Function}     props.onSubmit      – called with answersJson
 * @param {string}       props.appointmentId
 * @param {string}       props.formTemplateId
 */
export default function DynamicForm({
  schema,
  onSubmit,
  appointmentId,
  formTemplateId,
}) {
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});

  // 1️⃣ schema should already be parsed
  let form = schema;

  // 2️⃣ if string, parse
  if (typeof form === "string") {
    try {
      form = JSON.parse(form);
    } catch (err) {
      console.error("Couldn't JSON-parse form:", err);
      return <p className="qp-no-form">Invalid form schema.</p>;
    }
  }

  // 3️⃣ guard: must have fields array
  if (!form || !Array.isArray(form.fields)) {
    return (
      <div className="qp-no-form">
        <p>No form schema available.</p>
        <details>
          <summary>Debug Info</summary>
          <pre>
            {JSON.stringify(
              {
                form,
                hasFields: !!form?.fields,
                isArray: Array.isArray(form?.fields),
              },
              null,
              2
            )}
          </pre>
        </details>
      </div>
    );
  }

  // 4️⃣ helpers
  const validateField = (field, value) => {
    if (field.required && !value) return "This field is required";
    if (
      field.type === "email" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    )
      return "Please enter a valid email address";
    if (
      field.type === "phone" &&
      value &&
      !/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/.test(value)
    )
      return "Please enter a valid phone number (123-456-7890)";
    return null;
  };

  // update: strip event objects, only store primitives or safe objects
  const update = (id, rawVal) => {
    let val;
    if (rawVal && rawVal.target !== undefined) {
      // DOM event
      const t = rawVal.target;
      val = t.type === "checkbox" ? t.checked : t.value;
    } else {
      val = rawVal;
    }

    setValues((prev) => {
      const newValues = { ...prev, [id]: val };
      // validate
      const field = form.fields.find((f) => f.id === id);
      if (field) {
        const error = validateField(field, val);
        setErrors((prevErr) => ({ ...prevErr, [id]: error }));
      }
      return newValues;
    });
  };

  const handleFileUpload = async (field, files) => {
    const file = files[0];
    if (!file) return;

    const fileType = file.name.split(".").pop().toLowerCase();
    if (!field.acceptedFileTypes.includes(fileType)) {
      alert(
        `Please upload a valid file type: ${field.acceptedFileTypes.join(", ")}`
      );
      return;
    }

    const maxSize = field.maxFileSize * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${field.maxFileSize}MB`);
      return;
    }

    try {
      setUploadProgress((prev) => ({ ...prev, [field.id]: 0 }));

      const ts = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath =
        `appointments/${appointmentId}/forms/${formTemplateId}` +
        `/fields/${field.id}/${ts}-${safeName}`;

      const { error } = await supabase.storage
        .from("intake-documents")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          metadata: {
            appointment_id: appointmentId,
            form_template_id: formTemplateId,
            field_id: field.id,
            original_filename: file.name,
            uploaded_at: new Date().toISOString(),
            status: "pending",
          },
        });
      if (error) throw error;

      // store only path & filename
      update(field.id, {
        path: filePath,
        filename: file.name,
        status: "pending",
      });

      setUploadProgress((prev) => ({ ...prev, [field.id]: 100 }));
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload file. Please try again.");
      setUploadProgress((prev) => ({ ...prev, [field.id]: 0 }));
    }
  };

  const renderField = (f) => {
    const commonProps = {
      id: f.id,
      name: f.id,
      required: f.required,
      value: values[f.id] ?? "",
      onChange: (val) => update(f.id, val),
      placeholder: f.placeholder || "",
      helpText: f.helpText || "",
      error: errors[f.id],
      className: errors[f.id] ? "qp-error" : "",
    };

    switch (f.type) {
      case "file":
        return (
          <FileUploader
            {...commonProps}
            accept={f.acceptedFileTypes.map((t) => `.${t}`).join(",")}
            multiple={f.allowMultiple}
            onFilesSelected={(files) => handleFileUpload(f, files)}
          />
        );

      case "textarea":
        return (
          <TextAreaWithCount
            {...commonProps}
            maxLength={f.maxLength || 1000}
            rows={f.rows || 4}
          />
        );

      case "select":
        return (
          <NarrowDropdown2
            {...commonProps}
            options={f.options || []}
            allowMultiple={f.allowMultiple}
            placeholder="Select an option"
          />
        );

      case "date":
        return (
          <NarrowDatePicker
            {...commonProps}
            minDate={f.minDate}
            maxDate={f.maxDate}
          />
        );

      case "checkbox":
        return (
          <IconCheckbox
            {...commonProps}
            checked={!!values[f.id]}
            label={f.label}
          />
        );

      case "radio":
        return (
          <div className="qp-radio-group">
            {f.options.map((opt) => (
              <RadioButton
                key={opt}
                {...commonProps}
                value={opt}
                checked={values[f.id] === opt}
                label={opt}
              />
            ))}
          </div>
        );

      case "email":
        return (
          <NarrowInputField
            {...commonProps}
            type="text"
            onChange={(e) => update(f.id, e.target.value)}
          />
        );

      case "phone":
        return (
          <NarrowInputField
            {...commonProps}
            type="tel"
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            placeholder="123-456-7890"
          />
        );

      default:
        return <NarrowInputField {...commonProps} type="text" />;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    form.fields.forEach((field) => {
      const err = validateField(field, values[field.id]);
      if (err) newErrors[field.id] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    Promise.resolve(onSubmit(values))
      .catch((err) => console.error("Submit error:", err))
      .finally(() => setSubmitting(false));
  };

  const sections = form.fields.reduce((acc, field) => {
    const sec = field.section || "default";
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(field);
    return acc;
  }, {});

  return (
    <form className="qp-form" onSubmit={handleSubmit}>
      <h1 className="qp-title">{form.title || "Intake Form"}</h1>
      {form.description && <p className="qp-desc">{form.description}</p>}

      {Object.entries(sections).map(([secName, fields]) => (
        <div key={secName} className="qp-section">
          {secName !== "default" && (
            <h2 className="qp-section-title">{secName}</h2>
          )}
          {fields.map((f) => (
            <div key={f.id} className="qp-field">
              <label htmlFor={f.id} className="qp-label">
                {f.label} {f.required && <span className="qp-required">*</span>}
              </label>
              {renderField(f)}
              {f.helpText && <p className="qp-help-text">{f.helpText}</p>}
              {errors[f.id] && <p className="qp-error">{errors[f.id]}</p>}
            </div>
          ))}
        </div>
      ))}

      <button
        type="submit"
        className={`qp-btn ${submitting ? "qp-loading" : ""}`}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <span className="qp-spinner" />
            <span>Submitting...</span>
          </>
        ) : (
          "Submit"
        )}
      </button>
    </form>
  );
}
